// 自建self_lodash库 避免使用lodash库太大，导致性能下降
/**
 * 防抖函数
 * @param {Function} func - 要执行的函数
 * @param {number} wait - 延迟时间（毫秒）
 * @param {Object} [options] - 可选参数
 * @param {boolean} [options.leading=false] - 是否在延迟开始时立即调用
 * @param {boolean} [options.trailing=true] - 是否在延迟结束时调用
 * @returns {Function} - 新的防抖函数
 */
export function debounce(func, wait, options = {}) {
    if (typeof func !== 'function') {
        throw new TypeError('Expected a function');
    }

    let timeout;
    let lastArgs;
    let lastThis;
    let result;

    const { leading = false, trailing = true } = options;

    const invokeFunc = () => {
        if (lastArgs) {
            result = func.apply(lastThis, lastArgs);
            lastArgs = lastThis = null;
        }
        return result;
    };

    const later = () => {
        timeout = null;
        if (trailing && lastArgs) {
            invokeFunc();
        }
    };

    const debounced = function (...args) {
        lastArgs = args;
        lastThis = this;

        if (timeout) {
            clearTimeout(timeout);
        }

        if (leading && !timeout) {
            result = invokeFunc();
        }

        timeout = setTimeout(later, wait);
        return result;
    };

    debounced.cancel = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
        lastArgs = lastThis = null;
    };

    debounced.flush = () => {
        if (timeout) {
            clearTimeout(timeout);
            later();
        }
    };

    return debounced;
}

/**
 * 深度克隆
 * @param {any} value - 要克隆的值
 * @param {WeakMap} cache - 缓存对象
 * @returns {any} - 克隆后的值
 */
export function cloneDeep(value, cache = new WeakMap()) {
    // 处理 null 和基本数据类型
    if (value === null || typeof value !== 'object') {
        return value;
    }

    // 处理循环引用
    if (cache.has(value)) {
        return cache.get(value);
    }

    // 处理 Date
    if (value instanceof Date) {
        return new Date(value);
    }

    // 处理 RegExp
    if (value instanceof RegExp) {
        return new RegExp(value.source, value.flags);
    }

    // 处理 Array
    if (Array.isArray(value)) {
        const arrCopy = [];
        cache.set(value, arrCopy);
        for (let i = 0; i < value.length; i++) {
            arrCopy[i] = cloneDeep(value[i], cache);
        }
        return arrCopy;
    }

    // 处理 Object
    const objCopy = Object.create(Object.getPrototypeOf(value));
    cache.set(value, objCopy);
    for (const key in value) {
        if (value.hasOwnProperty(key)) {
            objCopy[key] = cloneDeep(value[key], cache);
        }
    }

    // 处理 Map
    if (value instanceof Map) {
        const mapCopy = new Map();
        cache.set(value, mapCopy);
        value.forEach((val, key) => {
            mapCopy.set(cloneDeep(key, cache), cloneDeep(val, cache));
        });
        return mapCopy;
    }

    // 处理 Set
    if (value instanceof Set) {
        const setCopy = new Set();
        cache.set(value, setCopy);
        value.forEach(item => {
            setCopy.add(cloneDeep(item, cache));
        });
        return setCopy;
    }

    // 处理其他类型，返回原始值
    return value;
}
